import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, Clock, Users, Star, BookOpen, Award, Check, 
  ChevronDown, ChevronRight, Lock, XCircle, 
  AlertCircle, CheckCircle2, Calendar, Quote, ThumbsUp, Radio, Video
} from 'lucide-react';
import { coursesApi, enrollmentsApi, paymentsApi, meetingsApi, MeetingData } from '../lib/api';
import { joinMeetingSecure } from '../lib/jitsi';
import { useAuthStore } from '../store/auth';
import { formatPrice, formatMinutes, getLevelColor, formatDate, formatRelativeTime } from '../lib/utils';
import { PageLoader, InlineLoader, SectionLoader } from '../components/ui/PremiumLoader';
import toast from 'react-hot-toast';
import { useAnalytics, AnalyticsEvents } from '../hooks/useAnalytics';

interface Course {
  id: string;
  slug: string;
  title: string;
  description: string;
  shortDescription?: string;
  thumbnailUrl?: string;
  previewVideoUrl?: string;
  price: number;
  currency: string;
  level: string;
  language: string;
  requirements: string[];
  whatYouWillLearn: string[];
  rating?: number;
  totalReviews: number;
  totalStudents: number;
  totalDuration?: number;
  totalLessons: number;
  hasAccess?: boolean;
  courseType?: 'RECORDED' | 'LIVE';
  professor: {
    id: string;
    firstName: string;
    lastName: string;
    avatarUrl?: string;
    bio?: string;
    _count?: {
      courses: number;
    };
  };
  category?: {
    name: string;
  };
  sections?: Array<{
    id: string;
    title: string;
    description?: string;
    lessons: Array<{
      id: string;
      title: string;
      type: string;
      duration?: number;
      isFree: boolean;
      contentUrl?: string;
    }>;
  }>;
}

interface Review {
  id: string;
  rating: number;
  comment?: string;
  createdAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    avatarUrl?: string;
  };
}

interface EnrollmentInfo {
  id: string;
  status: 'ACTIVE' | 'COMPLETED' | 'CANCELLED';
  progress: number;
  enrolledAt: string;
  completedAt?: string;
  canCancel: boolean;
  hoursRemaining: number;
}

interface CancelModalProps {
  enrollmentInfo: EnrollmentInfo;
  courseName: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
}

// Simple markdown-like parser for course descriptions
function parseDescription(text: string): JSX.Element[] {
  const lines = text.split('\n');
  const elements: JSX.Element[] = [];
  let listItems: string[] = [];
  let listType: 'numbered' | 'bullet' | null = null;
  let currentIndex = 0;

  const flushList = () => {
    if (listItems.length > 0) {
      if (listType === 'numbered') {
        elements.push(
          <ol key={`list-${currentIndex++}`} className="list-decimal list-inside space-y-2 my-4 ml-4">
            {listItems.map((item, i) => (
              <li key={i} className="text-white/70">{item}</li>
            ))}
          </ol>
        );
      } else {
        elements.push(
          <ul key={`list-${currentIndex++}`} className="space-y-2 my-4 ml-4">
            {listItems.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-white/70">
                <span className="w-1.5 h-1.5 rounded-full bg-coral mt-2 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        );
      }
      listItems = [];
      listType = null;
    }
  };

  const parseInlineFormatting = (text: string): JSX.Element => {
    // Parse **bold** text
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return (
      <>
        {parts.map((part, i) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i} className="text-white font-semibold">{part.slice(2, -2)}</strong>;
          }
          return <span key={i}>{part}</span>;
        })}
      </>
    );
  };

  for (const line of lines) {
    const trimmedLine = line.trim();
    
    // Empty line
    if (!trimmedLine) {
      flushList();
      continue;
    }

    // Numbered list (1. item)
    const numberedMatch = trimmedLine.match(/^\d+\.\s+(.+)$/);
    if (numberedMatch) {
      if (listType !== 'numbered') {
        flushList();
        listType = 'numbered';
      }
      listItems.push(numberedMatch[1]);
      continue;
    }

    // Bullet list (- item)
    const bulletMatch = trimmedLine.match(/^[-•]\s+(.+)$/);
    if (bulletMatch) {
      if (listType !== 'bullet') {
        flushList();
        listType = 'bullet';
      }
      listItems.push(bulletMatch[1]);
      continue;
    }

    // Regular paragraph or heading
    flushList();
    
    // Check for bold headings like **Heading:**
    if (trimmedLine.startsWith('**') && trimmedLine.includes(':**')) {
      const headingText = trimmedLine.replace(/\*\*/g, '').replace(/:$/, '');
      elements.push(
        <h3 key={`h-${currentIndex++}`} className="text-lg font-semibold text-white mt-6 mb-3">
          {headingText}
        </h3>
      );
    } else {
      elements.push(
        <p key={`p-${currentIndex++}`} className="text-white/70 leading-relaxed mb-3">
          {parseInlineFormatting(trimmedLine)}
        </p>
      );
    }
  }

  flushList();
  return elements;
}

function CancelModal({ enrollmentInfo, courseName, onClose, onConfirm, isLoading }: CancelModalProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-md rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #18181f 0%, #111116 100%)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-rose-500/15 flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-rose-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Cancel Enrollment</h3>
              <p className="text-sm text-white/40">This action cannot be undone</p>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.04] mb-4">
            <p className="text-white/70 text-sm">
              Are you sure you want to cancel your enrollment in{' '}
              <span className="text-white font-medium">{courseName}</span>?
            </p>
            {Number(enrollmentInfo.progress) > 0 && (
              <p className="text-amber-400 text-sm mt-2">
                ⚠️ You have {Math.round(Number(enrollmentInfo.progress))}% progress that will be lost.
              </p>
            )}
          </div>

          {enrollmentInfo.canCancel ? (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 mb-6">
              <Clock className="w-4 h-4 text-amber-400 flex-shrink-0" />
              <p className="text-amber-400 text-sm">
                {enrollmentInfo.hoursRemaining.toFixed(1)} hours remaining to cancel
              </p>
            </div>
          ) : (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 mb-6">
              <XCircle className="w-4 h-4 text-rose-400 flex-shrink-0" />
              <p className="text-rose-400 text-sm">
                Cancellation window has expired (24 hours)
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-3 rounded-xl font-medium text-white/70 
                       bg-white/[0.04] border border-white/[0.06]
                       hover:bg-white/[0.08] transition-colors"
            >
              Keep Enrolled
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading || !enrollmentInfo.canCancel}
              className="flex-1 px-4 py-3 rounded-xl font-medium text-white 
                       bg-rose-500/80 hover:bg-rose-500 
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-colors flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <InlineLoader />
                  <span className="ml-2">Cancelling...</span>
                </>
              ) : (
                'Cancel Enrollment'
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Review Card Component
function ReviewCard({ review, index }: { review: Review; index: number }) {
  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < rating ? 'text-amber-400 fill-amber-400' : 'text-white/20'
        }`}
      />
    ));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="p-5 rounded-2xl"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.04)',
      }}
    >
      <div className="flex items-start gap-4">
        {review.user.avatarUrl ? (
          <img
            src={review.user.avatarUrl}
            alt={review.user.firstName}
            className="w-12 h-12 rounded-full object-cover"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-coral to-coral/60 flex items-center justify-center text-white font-semibold">
            {review.user.firstName.charAt(0)}{review.user.lastName.charAt(0)}
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <h4 className="font-medium text-white">
              {review.user.firstName} {review.user.lastName}
            </h4>
            <span className="text-xs text-white/40">
              {formatRelativeTime(review.createdAt)}
            </span>
          </div>
          
          <div className="flex items-center gap-1 mb-3">
            {renderStars(review.rating)}
          </div>
          
          {review.comment && (
            <p className="text-white/60 text-sm leading-relaxed">
              {review.comment}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// Rating Summary Component
function RatingSummary({ rating, totalReviews }: { rating: number; totalReviews: number }) {
  const ratingNum = Number(rating) || 0;
  
  // Mock distribution - in real app, fetch from API
  const distribution = [
    { stars: 5, percent: 70 },
    { stars: 4, percent: 20 },
    { stars: 3, percent: 7 },
    { stars: 2, percent: 2 },
    { stars: 1, percent: 1 },
  ];

  return (
    <div className="flex flex-col sm:flex-row gap-8 p-6 rounded-2xl mb-6"
      style={{
        background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.08) 0%, rgba(251, 191, 36, 0.02) 100%)',
        border: '1px solid rgba(251, 191, 36, 0.15)',
      }}
    >
      {/* Overall Rating */}
      <div className="text-center sm:text-left">
        <div className="text-5xl font-bold text-white mb-2">
          {ratingNum.toFixed(1)}
        </div>
        <div className="flex items-center justify-center sm:justify-start gap-1 mb-2">
          {Array.from({ length: 5 }, (_, i) => (
            <Star
              key={i}
              className={`w-5 h-5 ${
                i < Math.round(ratingNum) ? 'text-amber-400 fill-amber-400' : 'text-white/20'
              }`}
            />
          ))}
        </div>
        <p className="text-white/50 text-sm">
          {totalReviews.toLocaleString()} reviews
        </p>
      </div>

      {/* Rating Distribution */}
      <div className="flex-1 space-y-2">
        {distribution.map((item) => (
          <div key={item.stars} className="flex items-center gap-3">
            <div className="flex items-center gap-1 w-16">
              <span className="text-sm text-white/60">{item.stars}</span>
              <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
            </div>
            <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${item.percent}%` }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="h-full rounded-full bg-amber-400"
              />
            </div>
            <span className="text-sm text-white/40 w-12 text-right">{item.percent}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CourseDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const { trackEvent, trackCourseViewed, trackMeetingJoined } = useAnalytics();
  const [course, setCourse] = useState<Course | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [enrollmentInfo, setEnrollmentInfo] = useState<EnrollmentInfo | null>(null);
  const [isCheckingEnrollment, setIsCheckingEnrollment] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [meetings, setMeetings] = useState<MeetingData[]>([]);
  const [isLoadingMeetings, setIsLoadingMeetings] = useState(false);
  const [isJoiningMeeting, setIsJoiningMeeting] = useState(false);

  // Fetch course details
  useEffect(() => {
    const fetchCourse = async () => {
      if (!slug) return;
      setIsLoading(true);
      try {
        const response = await coursesApi.getById(slug, true);
        if (response.data.success) {
          const courseData = response.data.data as Course;
          setCourse(courseData);
          
          // Track course view
          trackCourseViewed(
            courseData.id,
            courseData.title,
            courseData.courseType || 'RECORDED',
            courseData.price
          );
        }
      } catch (error) {
        console.error('Failed to fetch course:', error);
        toast.error('Course not found');
        navigate('/courses');
      } finally {
        setIsLoading(false);
      }
    };
    fetchCourse();
  }, [slug, navigate, trackCourseViewed]);

  // Fetch reviews
  useEffect(() => {
    const fetchReviews = async () => {
      if (!course?.id) return;
      setIsLoadingReviews(true);
      try {
        const response = await coursesApi.getReviews(course.id, { limit: 10 });
        if (response.data.success) {
          setReviews(response.data.data as Review[] || []);
        }
      } catch (error) {
        console.error('Failed to fetch reviews:', error);
      } finally {
        setIsLoadingReviews(false);
      }
    };
    fetchReviews();
  }, [course?.id]);

  // Fetch meetings for live courses
  useEffect(() => {
    const fetchMeetings = async () => {
      if (!course?.id || course.courseType !== 'LIVE') return;
      setIsLoadingMeetings(true);
      try {
        const response = await meetingsApi.getByCourse(course.id);
        if (response.data.success) {
          setMeetings(response.data.data || []);
        }
      } catch (error) {
        console.error('Failed to fetch meetings:', error);
      } finally {
        setIsLoadingMeetings(false);
      }
    };
    fetchMeetings();
  }, [course?.id, course?.courseType]);

  // Check enrollment status
  useEffect(() => {
    const checkEnrollment = async () => {
      if (!course?.id || !isAuthenticated) {
        setEnrollmentInfo(null);
        return;
      }
      
      setIsCheckingEnrollment(true);
      try {
        const response = await enrollmentsApi.check(course.id);
        if (response.data.success && response.data.data?.isEnrolled) {
          const enrollment = response.data.data.enrollment;
          if (enrollment) {
            const enrolledAt = new Date(enrollment.enrolledAt);
            const now = new Date();
            const hoursSinceEnrollment = (now.getTime() - enrolledAt.getTime()) / (1000 * 60 * 60);
            const hoursRemaining = Math.max(0, 24 - hoursSinceEnrollment);
            
            const hasStarted = Number(enrollment.progress) > 0;
            setEnrollmentInfo({
              id: enrollment.id,
              status: enrollment.status,
              progress: Number(enrollment.progress),
              enrolledAt: enrollment.enrolledAt,
              completedAt: enrollment.completedAt,
              canCancel: enrollment.status === 'ACTIVE' && hoursRemaining > 0 && !hasStarted,
              hoursRemaining,
            });
          }
        } else {
          setEnrollmentInfo(null);
        }
      } catch (error) {
        console.error('Failed to check enrollment:', error);
        setEnrollmentInfo(null);
      } finally {
        setIsCheckingEnrollment(false);
      }
    };
    
    checkEnrollment();
  }, [course?.id, isAuthenticated]);

  const handleEnroll = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to enroll');
      navigate('/login', { state: { from: { pathname: `/courses/${slug}` } } });
      return;
    }

    if (!course) return;

    setIsEnrolling(true);
    try {
      if (course.price === 0) {
        const response = await enrollmentsApi.enroll(course.id);
        if (response.data.success) {
          // Track free enrollment
          trackEvent(AnalyticsEvents.COURSE_ENROLLED_FREE, {
            courseId: course.id,
            courseTitle: course.title,
            courseType: course.courseType || 'RECORDED',
            level: course.level,
          });
          toast.success('Successfully enrolled!');
          navigate('/student/my-courses?enrolled=true');
        }
      } else {
        // Track checkout started
        trackEvent(AnalyticsEvents.CHECKOUT_STARTED, {
          courseId: course.id,
          courseTitle: course.title,
          courseType: course.courseType || 'RECORDED',
          price: course.price,
          currency: course.currency,
        });
        
        const response = await paymentsApi.createCheckout({
          courseId: course.id,
          successUrl: `${window.location.origin}/student/my-courses?enrolled=true`,
          cancelUrl: `${window.location.origin}/courses/${slug}`,
        });
        
        if (response.data.success && response.data.data?.url) {
          window.location.href = response.data.data.url;
        }
      }
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error 
        || 'Failed to enroll';
      toast.error(message);
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleCancelEnrollment = async () => {
    if (!enrollmentInfo || !course) return;

    setIsCancelling(true);
    try {
      const response = await enrollmentsApi.cancel(enrollmentInfo.id);
      if (response.data.success) {
        // Track enrollment cancellation
        trackEvent(AnalyticsEvents.ENROLLMENT_CANCELLED, {
          courseId: course.id,
          courseTitle: course.title,
          progressAtCancellation: enrollmentInfo.progress,
          hoursAfterEnrollment: 24 - enrollmentInfo.hoursRemaining,
        });
        
        toast.success('Enrollment cancelled successfully');
        setShowCancelModal(false);
        setEnrollmentInfo(null);
        const courseResponse = await coursesApi.getById(slug!, true);
        if (courseResponse.data.success) {
          setCourse(courseResponse.data.data as Course);
        }
      }
    } catch (error: unknown) {
      console.error('Failed to cancel enrollment:', error);
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error 
        || 'Failed to cancel enrollment';
      toast.error(message);
    } finally {
      setIsCancelling(false);
    }
  };

  const toggleSection = (sectionId: string) => {
    const isExpanding = !expandedSections.includes(sectionId);
    
    if (isExpanding && course) {
      // Track section expansion
      trackEvent(AnalyticsEvents.COURSE_SECTION_EXPANDED, {
        courseId: course.id,
        sectionId,
      });
    }
    
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleJoinMeeting = async (meetingId: string) => {
    setIsJoiningMeeting(true);
    try {
      // Track meeting join
      if (course) {
        trackMeetingJoined(meetingId, course.id, false);
      }
      await joinMeetingSecure(meetingId);
    } catch (error) {
      console.error('Failed to join meeting:', error);
      toast.error('Failed to join meeting');
    } finally {
      setIsJoiningMeeting(false);
    }
  };

  // Helper functions for meetings
  const liveMeeting = meetings.find(m => m.status === 'LIVE');
  const upcomingMeetings = meetings.filter(m => m.status === 'SCHEDULED').slice(0, 3);
  const pastMeetings = meetings.filter(m => m.status === 'ENDED').slice(0, 5);

  if (isLoading) {
    return <PageLoader text="Loading course..." />;
  }

  if (!course) {
    return null;
  }

  const isOwner = user?.id === course.professor.id;
  const isEnrolled = !!enrollmentInfo && enrollmentInfo.status !== 'CANCELLED';
  const hasAccess = course.hasAccess || isEnrolled || isOwner;
  const displayedReviews = showAllReviews ? reviews : reviews.slice(0, 3);

  return (
    <div className="min-h-screen bg-dark">
      {/* Hero Section */}
      <section className="bg-dark-100 py-6 sm:py-8 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Left content */}
            <div className="lg:col-span-2 order-2 lg:order-1">
              {/* Breadcrumb */}
              <div className="hidden sm:flex items-center gap-2 text-sm text-text-muted mb-4 overflow-x-auto">
                <Link to="/courses" className="hover:text-text-primary flex-shrink-0">Courses</Link>
                <ChevronRight className="w-4 h-4 flex-shrink-0" />
                {course.category && (
                  <>
                    <span className="flex-shrink-0">{course.category.name}</span>
                    <ChevronRight className="w-4 h-4 flex-shrink-0" />
                  </>
                )}
                <span className="text-text-secondary truncate">{course.title}</span>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-text-primary">
                  {course.title}
                </h1>

                {course.shortDescription && (
                  <p className="mt-4 text-lg text-text-muted">
                    {course.shortDescription}
                  </p>
                )}

                {/* Stats */}
                <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-4 sm:mt-6">
                  <span className={`badge text-xs ${getLevelColor(course.level)}`}>
                    {course.level}
                  </span>
                  
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 fill-yellow-500" />
                    <span className="text-text-primary font-medium text-sm sm:text-base">
                      {course.rating ? Number(course.rating).toFixed(1) : 'New'}
                    </span>
                    <span className="text-text-muted text-xs sm:text-sm">
                      ({course.totalReviews})
                    </span>
                  </div>

                  <div className="flex items-center gap-1 text-text-muted text-xs sm:text-sm">
                    <Users className="w-4 h-4 sm:w-5 sm:h-5" />
                    {course.totalStudents.toLocaleString()}
                  </div>

                  {course.totalDuration && (
                    <div className="flex items-center gap-1 text-text-muted text-xs sm:text-sm">
                      <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                      {formatMinutes(course.totalDuration)}
                    </div>
                  )}
                </div>

                {/* Enrollment Status Banner */}
                {isEnrolled && enrollmentInfo && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-6 p-4 rounded-xl"
                    style={{
                      background: enrollmentInfo.status === 'COMPLETED'
                        ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%)'
                        : 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(99, 102, 241, 0.05) 100%)',
                      border: enrollmentInfo.status === 'COMPLETED'
                        ? '1px solid rgba(16, 185, 129, 0.25)'
                        : '1px solid rgba(99, 102, 241, 0.25)',
                    }}
                  >
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                      <div className="flex items-center gap-3">
                        {enrollmentInfo.status === 'COMPLETED' ? (
                          <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                        ) : (
                          <Play className="w-5 h-5 text-indigo-400 flex-shrink-0" />
                        )}
                        <div>
                          <p className={`font-medium text-sm sm:text-base ${enrollmentInfo.status === 'COMPLETED' ? 'text-emerald-400' : 'text-indigo-400'}`}>
                            {enrollmentInfo.status === 'COMPLETED' 
                              ? 'Course Completed!' 
                              : `${Math.round(enrollmentInfo.progress)}% Complete`}
                          </p>
                          <p className="text-xs sm:text-sm text-white/40">
                            Enrolled {formatRelativeTime(enrollmentInfo.enrolledAt)}
                            {enrollmentInfo.completedAt && ` • Completed ${formatRelativeTime(enrollmentInfo.completedAt)}`}
                          </p>
                        </div>
                      </div>
                      
                      {enrollmentInfo.status === 'ACTIVE' && (
                        <div className="w-full sm:flex-1 sm:max-w-xs">
                          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${enrollmentInfo.progress}%` }}
                              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                              className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {enrollmentInfo.canCancel && (
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/[0.06]">
                        <div className="flex items-center gap-2 text-amber-400 text-sm">
                          <Clock className="w-4 h-4" />
                          <span>{enrollmentInfo.hoursRemaining.toFixed(1)} hours left to cancel</span>
                        </div>
                        <button
                          onClick={() => setShowCancelModal(true)}
                          className="text-sm text-rose-400 hover:text-rose-300 transition-colors flex items-center gap-1"
                        >
                          <XCircle className="w-4 h-4" />
                          Cancel Enrollment
                        </button>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Professor */}
                <div className="flex items-center gap-3 mt-6">
                  {course.professor.avatarUrl ? (
                    <img
                      src={course.professor.avatarUrl}
                      alt={course.professor.firstName}
                      className="w-12 h-12 rounded-full object-cover ring-2 ring-white/10"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gradient-accent rounded-full flex items-center justify-center text-white font-semibold">
                      {course.professor.firstName.charAt(0)}
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-white/40">Instructor</p>
                    <Link 
                      to={`/users/${course.professor.id}`}
                      className="text-text-primary font-medium hover:text-accent-light"
                    >
                      {course.professor.firstName} {course.professor.lastName}
                    </Link>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Right - Purchase card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="order-1 lg:order-2 lg:sticky lg:top-24"
            >
              <div className="card">
                <div className="aspect-video rounded-xl overflow-hidden mb-4 bg-dark-200">
                  {course.thumbnailUrl ? (
                    <img
                      src={course.thumbnailUrl}
                      alt={course.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Play className="w-12 h-12 text-text-muted" />
                    </div>
                  )}
                </div>

                {!isEnrolled && (
                  <div className="text-3xl font-bold text-text-primary mb-4">
                    {course.price === 0 ? 'Free' : formatPrice(course.price, course.currency)}
                  </div>
                )}

                {isCheckingEnrollment ? (
                  <div className="btn-secondary w-full justify-center">
                    <InlineLoader />
                    <span className="ml-2">Checking enrollment...</span>
                  </div>
                ) : isEnrolled ? (
                  <div className="space-y-3">
                    {course.courseType === 'LIVE' ? (
                      <>
                        {/* Live Meeting in Progress - Show Join Button */}
                        {liveMeeting ? (
                          <div className="p-4 rounded-xl bg-gradient-to-r from-red-500/15 to-purple-500/10 border border-red-500/30">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                              <span className="font-semibold text-red-400">Live Now!</span>
                            </div>
                            <p className="text-sm text-white font-medium mb-3">
                              {liveMeeting.title}
                            </p>
                            <button
                              onClick={() => handleJoinMeeting(liveMeeting.id)}
                              disabled={isJoiningMeeting}
                              className="w-full py-3 px-4 rounded-xl font-semibold text-white 
                                       bg-red-500 hover:bg-red-600 transition-colors
                                       flex items-center justify-center gap-2
                                       disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isJoiningMeeting ? (
                                <>
                                  <InlineLoader />
                                  <span>Connecting...</span>
                                </>
                              ) : (
                                <>
                                  <Radio className="w-5 h-5" />
                                  Join Live Session
                                </>
                              )}
                            </button>
                          </div>
                        ) : upcomingMeetings.length > 0 ? (
                          // Upcoming meetings
                          <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/20">
                            <div className="flex items-center gap-2 mb-3">
                              <Calendar className="w-5 h-5 text-purple-400" />
                              <span className="font-semibold text-purple-300">Upcoming Sessions</span>
                            </div>
                            <div className="space-y-2">
                              {upcomingMeetings.map((meeting) => (
                                <div key={meeting.id} className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.04]">
                                  <p className="text-sm text-white font-medium truncate">{meeting.title}</p>
                                  <p className="text-xs text-white/50 mt-1 flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {new Date(meeting.scheduledAt).toLocaleDateString('en-US', { 
                                      weekday: 'short', month: 'short', day: 'numeric' 
                                    })} at {new Date(meeting.scheduledAt).toLocaleTimeString([], { 
                                      hour: '2-digit', minute: '2-digit' 
                                    })}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          // No meetings scheduled
                          <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/20">
                            <div className="flex items-center gap-2 mb-2">
                              <Radio className="w-5 h-5 text-purple-400" />
                              <span className="font-semibold text-purple-300">Live Session Course</span>
                            </div>
                            <p className="text-sm text-white/60">
                              No sessions scheduled yet. The instructor will schedule sessions soon.
                            </p>
                          </div>
                        )}
                        <Link
                          to="/student/my-courses"
                          className="btn-primary w-full justify-center"
                          style={{ background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)' }}
                        >
                          <Calendar className="w-5 h-5 mr-2" />
                          View My Courses
                        </Link>
                      </>
                    ) : (
                      <Link
                        to={`/student/course/${enrollmentInfo?.id}`}
                        className="btn-primary w-full justify-center"
                      >
                        <Play className="w-5 h-5 mr-2" />
                        {enrollmentInfo?.status === 'COMPLETED' ? 'Review Course' : 'Continue Learning'}
                      </Link>
                    )}
                    <Link
                      to="/student/my-courses"
                      className="btn-secondary w-full justify-center"
                    >
                      <BookOpen className="w-5 h-5 mr-2" />
                      My Courses
                    </Link>
                  </div>
                ) : isOwner ? (
                  <Link
                    to={`/professor/courses/${course.id}/edit`}
                    className="btn-primary w-full justify-center"
                  >
                    Edit Course
                  </Link>
                ) : (
                  <button
                    onClick={handleEnroll}
                    disabled={isEnrolling}
                    className="btn-primary w-full justify-center"
                  >
                    {isEnrolling ? (
                      <>
                        <InlineLoader className="mr-2" />
                        Processing...
                      </>
                    ) : (
                      course.price === 0 ? 'Enroll for Free' : 'Enroll Now'
                    )}
                  </button>
                )}

                <ul className="mt-6 space-y-3 text-sm">
                  {course.courseType === 'LIVE' ? (
                    <li className="flex items-center gap-2 text-purple-400">
                      <Radio className="w-4 h-4" />
                      Live Sessions
                    </li>
                  ) : (
                    <li className="flex items-center gap-2 text-text-muted">
                      <BookOpen className="w-4 h-4" />
                      {course.totalLessons} lessons
                    </li>
                  )}
                  {course.totalDuration && course.courseType !== 'LIVE' && (
                    <li className="flex items-center gap-2 text-text-muted">
                      <Clock className="w-4 h-4" />
                      {formatMinutes(course.totalDuration)} total
                    </li>
                  )}
                  <li className="flex items-center gap-2 text-text-muted">
                    <Award className="w-4 h-4" />
                    Certificate of completion
                  </li>
                  {isEnrolled && enrollmentInfo && (
                    <li className="flex items-center gap-2 text-text-muted">
                      <Calendar className="w-4 h-4" />
                      Enrolled {formatDate(enrollmentInfo.enrolledAt)}
                    </li>
                  )}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-6 sm:py-8 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="lg:col-span-2 space-y-8 sm:space-y-12">
              {/* What you'll learn */}
              {course.whatYouWillLearn && course.whatYouWillLearn.length > 0 && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-6 rounded-2xl"
                  style={{
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(16, 185, 129, 0.02) 100%)',
                    border: '1px solid rgba(16, 185, 129, 0.15)',
                  }}
                >
                  <h2 className="text-xl font-semibold text-white mb-5 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    What you'll learn
                  </h2>
                  <ul className="grid sm:grid-cols-2 gap-3">
                    {course.whatYouWillLearn.map((item, index) => (
                      <motion.li 
                        key={index} 
                        className="flex items-start gap-3"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span className="text-white/70">{item}</span>
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>
              )}

              {/* Live Sessions Section - For Live Courses */}
              {course.courseType === 'LIVE' && isEnrolled && (
                <div>
                  <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                    <Radio className="w-5 h-5 text-purple-400" />
                    Live Sessions
                  </h2>
                  
                  {isLoadingMeetings ? (
                    <SectionLoader text="Loading sessions..." />
                  ) : meetings.length > 0 ? (
                    <div className="space-y-4">
                      {/* Live Now Banner */}
                      {liveMeeting && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="p-5 rounded-2xl bg-gradient-to-r from-red-500/15 to-purple-500/10 border border-red-500/30"
                        >
                          <div className="flex items-center justify-between flex-wrap gap-4">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                                <Radio className="w-6 h-6 text-red-400 animate-pulse" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                                  <span className="text-red-400 font-semibold text-sm">LIVE NOW</span>
                                </div>
                                <h3 className="text-white font-semibold">{liveMeeting.title}</h3>
                              </div>
                            </div>
                            <button
                              onClick={() => handleJoinMeeting(liveMeeting.id)}
                              disabled={isJoiningMeeting}
                              className="px-6 py-3 rounded-xl font-semibold text-white 
                                       bg-red-500 hover:bg-red-600 transition-colors
                                       flex items-center gap-2
                                       disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isJoiningMeeting ? (
                                <>
                                  <InlineLoader />
                                  <span>Connecting...</span>
                                </>
                              ) : (
                                <>
                                  <Play className="w-5 h-5" />
                                  Join Now
                                </>
                              )}
                            </button>
                          </div>
                        </motion.div>
                      )}

                      {/* Upcoming Sessions */}
                      {upcomingMeetings.length > 0 && (
                        <div>
                          <h3 className="text-white/70 font-medium mb-3 flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-purple-400" />
                            Upcoming Sessions ({upcomingMeetings.length})
                          </h3>
                          <div className="space-y-2">
                            {upcomingMeetings.map((meeting, index) => {
                              const meetingDate = new Date(meeting.scheduledAt);
                              const isToday = new Date().toDateString() === meetingDate.toDateString();
                              
                              return (
                                <motion.div
                                  key={meeting.id}
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: index * 0.05 }}
                                  className="p-4 rounded-xl flex items-center justify-between"
                                  style={{
                                    background: 'rgba(255,255,255,0.02)',
                                    border: '1px solid rgba(255,255,255,0.04)',
                                  }}
                                >
                                  <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-purple-500/15 flex items-center justify-center">
                                      <Calendar className="w-5 h-5 text-purple-400" />
                                    </div>
                                    <div>
                                      <h4 className="text-white font-medium">{meeting.title}</h4>
                                      <p className="text-sm text-white/50 flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        {isToday ? 'Today' : meetingDate.toLocaleDateString('en-US', { 
                                          weekday: 'short', month: 'short', day: 'numeric' 
                                        })} at {meetingDate.toLocaleTimeString([], { 
                                          hour: '2-digit', minute: '2-digit' 
                                        })}
                                        <span className="mx-1">•</span>
                                        {meeting.duration} min
                                      </p>
                                    </div>
                                  </div>
                                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-500/15 text-purple-400">
                                    Scheduled
                                  </span>
                                </motion.div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Past Sessions */}
                      {pastMeetings.length > 0 && (
                        <div>
                          <h3 className="text-white/70 font-medium mb-3 flex items-center gap-2">
                            <Video className="w-4 h-4 text-white/40" />
                            Past Sessions ({pastMeetings.length})
                          </h3>
                          <div className="space-y-2">
                            {pastMeetings.map((meeting, index) => {
                              const meetingDate = new Date(meeting.scheduledAt);
                              
                              return (
                                <motion.div
                                  key={meeting.id}
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: index * 0.05 }}
                                  className="p-4 rounded-xl flex items-center justify-between"
                                  style={{
                                    background: 'rgba(255,255,255,0.015)',
                                    border: '1px solid rgba(255,255,255,0.03)',
                                  }}
                                >
                                  <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-white/[0.03] flex items-center justify-center">
                                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                                    </div>
                                    <div>
                                      <h4 className="text-white/70 font-medium">{meeting.title}</h4>
                                      <p className="text-sm text-white/40">
                                        {meetingDate.toLocaleDateString('en-US', { 
                                          month: 'short', day: 'numeric', year: 'numeric'
                                        })}
                                        {meeting.recordingUrl && (
                                          <span className="ml-2 text-purple-400">Recording available</span>
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                  {meeting.recordingUrl && (
                                    <a 
                                      href={meeting.recordingUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="px-3 py-1.5 rounded-lg text-xs font-medium bg-purple-500/15 text-purple-400 hover:bg-purple-500/25 transition-colors flex items-center gap-1"
                                    >
                                      <Play className="w-3 h-3" />
                                      Watch
                                    </a>
                                  )}
                                </motion.div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div 
                      className="text-center py-12 rounded-2xl"
                      style={{
                        background: 'rgba(255,255,255,0.02)',
                        border: '1px solid rgba(255,255,255,0.04)',
                      }}
                    >
                      <Calendar className="w-12 h-12 text-white/20 mx-auto mb-3" />
                      <p className="text-white/40">No sessions scheduled yet</p>
                      <p className="text-white/30 text-sm mt-1">The instructor will schedule live sessions soon.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Course Content */}
              {course.sections && course.sections.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-coral" />
                    Course Content
                  </h2>
                  <p className="text-white/40 text-sm mb-4">
                    {course.sections.length} sections • {course.totalLessons} lessons • {course.totalDuration ? formatMinutes(course.totalDuration) : '0h'} total
                  </p>
                  <div className="space-y-2">
                    {course.sections.map((section, sectionIndex) => (
                      <motion.div 
                        key={section.id} 
                        className="rounded-xl overflow-hidden"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: sectionIndex * 0.05 }}
                        style={{
                          background: 'rgba(255,255,255,0.02)',
                          border: '1px solid rgba(255,255,255,0.04)',
                        }}
                      >
                        <button
                          onClick={() => toggleSection(section.id)}
                          className="w-full flex items-center justify-between p-4 hover:bg-white/[0.02] transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <ChevronDown 
                              className={`w-5 h-5 text-white/40 transition-transform ${
                                expandedSections.includes(section.id) ? 'rotate-180' : ''
                              }`} 
                            />
                            <span className="font-medium text-white text-left">
                              {section.title}
                            </span>
                          </div>
                          <span className="text-sm text-white/40">
                            {section.lessons.length} lessons
                          </span>
                        </button>

                        <AnimatePresence>
                          {expandedSections.includes(section.id) && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="border-t border-white/[0.04]"
                            >
                              {section.lessons.map((lesson) => (
                                <div
                                  key={lesson.id}
                                  className="flex items-center justify-between px-4 py-3 pl-12 hover:bg-white/[0.02] transition-colors"
                                >
                                  <div className="flex items-center gap-3">
                                    {lesson.isFree || hasAccess ? (
                                      <Play className="w-4 h-4 text-coral" />
                                    ) : (
                                      <Lock className="w-4 h-4 text-white/30" />
                                    )}
                                    <span className="text-white/70">
                                      {lesson.title}
                                    </span>
                                    {lesson.isFree && !hasAccess && (
                                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-500/20 text-emerald-400">
                                        Preview
                                      </span>
                                    )}
                                  </div>
                                  {lesson.duration && (
                                    <span className="text-sm text-white/40">
                                      {formatMinutes(lesson.duration / 60)}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              <div>
                <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Quote className="w-5 h-5 text-coral" />
                  About This Course
                </h2>
                <div className="prose prose-invert max-w-none">
                  {parseDescription(course.description)}
                </div>
              </div>

              {/* Requirements */}
              {course.requirements && course.requirements.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-400" />
                    Requirements
                  </h2>
                  <ul className="space-y-2">
                    {course.requirements.map((req, index) => (
                      <motion.li 
                        key={index} 
                        className="flex items-start gap-3"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 flex-shrink-0" />
                        <span className="text-white/70">{req}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Reviews Section */}
              <div>
                <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                  Student Reviews
                </h2>

                {course.rating && course.totalReviews > 0 && (
                  <RatingSummary rating={course.rating} totalReviews={course.totalReviews} />
                )}

                {isLoadingReviews ? (
                  <SectionLoader text="Loading reviews..." />
                ) : reviews.length > 0 ? (
                  <>
                    <div className="space-y-4">
                      {displayedReviews.map((review, index) => (
                        <ReviewCard key={review.id} review={review} index={index} />
                      ))}
                    </div>
                    
                    {reviews.length > 3 && (
                      <button
                        onClick={() => setShowAllReviews(!showAllReviews)}
                        className="mt-6 w-full py-3 rounded-xl font-medium text-white/70 
                                 bg-white/[0.04] border border-white/[0.06]
                                 hover:bg-white/[0.08] transition-colors"
                      >
                        {showAllReviews ? 'Show Less' : `Show All ${reviews.length} Reviews`}
                      </button>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12 rounded-2xl"
                    style={{
                      background: 'rgba(255,255,255,0.02)',
                      border: '1px solid rgba(255,255,255,0.04)',
                    }}
                  >
                    <ThumbsUp className="w-12 h-12 text-white/20 mx-auto mb-3" />
                    <p className="text-white/40">No reviews yet</p>
                    <p className="text-white/30 text-sm mt-1">Be the first to review this course!</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cancel Modal */}
      <AnimatePresence>
        {showCancelModal && enrollmentInfo && (
          <CancelModal
            enrollmentInfo={enrollmentInfo}
            courseName={course.title}
            onClose={() => setShowCancelModal(false)}
            onConfirm={handleCancelEnrollment}
            isLoading={isCancelling}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
